import ScButton from "@/src/components/ui-kit/button";
import { useAuth } from "@/src/contexts/auth.context";
import { useDeleteUserMutation } from "@/src/services/users.blocks.api";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import Toast from "react-native-toast-message";
import ConfirmModal from "../../action-modals/confirm-modal";
import { useForgotPassword } from "@/src/hooks/features/auth/use-forgot-password";

const Account = () => {
  const { user, isLoading, logout, isSuperAdmin } = useAuth();
  const { t } = useTranslation();

  // Forgot Password Hook
  const {
    onSubmit: triggerForgotPassword,
    isSubmitting: isSendingRecoveryEmail,
  } = useForgotPassword();

  const [showDelete, setShowDelete] = useState(false);
  const { mutate: deleteUser, isPending } = useDeleteUserMutation();

  if (isLoading)
    return (
      <View className="p-6">
        <Text className="text-slate">{t("LABEL_LOADING")}</Text>
      </View>
    );

  if (!user)
    return (
      <View className="p-6">
        <Text className="text-slate">{t("LABEL_NO_USER_DATA")}</Text>
      </View>
    );

  const userEmail = user.email;

  // Handle Change Password
  const handleChangePassword = async () => {
    if (!userEmail) return;

    try {
      await triggerForgotPassword({ email: userEmail });
      // Success toast is already handled inside useForgotPassword
    } catch (error) {
      // Error handling is already managed in the hook
    }
  };

  const handleDelete = () => {
    if (!user?.itemId) return;
    // Same trigger as the super-admin "Delete User" flow (IdP Deactivate +
    // purgeUserData), scoped to the current user's own id.
    deleteUser(user.itemId, {
      onSuccess: async () => {
        setShowDelete(false);
        await logout();
      },
      onError: () => {
        setShowDelete(false);
        Toast.show({ type: "error", text1: t("ERROR_ACCOUNT_DELETE_FAILED") });
      },
    });
  };

  return (
    <View className="py-4">
      {/* Password & Security */}
      <View>
        <Text className="font-medium text-lg px-4 pb-4 text-onyx">
          {t("LABEL_PASSWORD_AND_SECURITY")}
        </Text>
        <View className="flex-col p-4 gap-3 border-t items-start border-ash-3">
          <Text className="font-medium text-onyx">{t("LABEL_MODIFY_PASSWORD")}</Text>
          
          <ScButton 
            text={isSendingRecoveryEmail ? t("LABEL_SENDING") || "Sending..." : "Change Password"} 
            onPress={handleChangePassword}
            disabled={isSendingRecoveryEmail}
            loading={isSendingRecoveryEmail}   // if your ScButton supports loading prop
          />

          <Text className="text-gray-dark">
            {t("HINT_PASSWORD_CHANGE_REDIRECT")}.
          </Text>
        </View>
      </View>

      {/* Account Deletion */}
      <View>
        <Text className="font-medium text-lg px-4 pb-4 mt-6 text-onyx">
          {t("LABEL_ACCOUNT_DELETION")}
        </Text>
        <View className="flex-col p-4 gap-3 border-t items-start border-ash-3">
          <Text className="font-medium text-onyx">{t("LABEL_CLOSE_ACCOUNT")}</Text>
          <View
            className={`rounded-[16px] ${isSuperAdmin ? "bg-ash-3" : "bg-red-dark"}`}
          >
            <ScButton
              text="Delete Account"
              backgroundColor="transparent"
              onPress={() => setShowDelete(true)}
              disabled={isSuperAdmin}
            />
          </View>
          {isSuperAdmin ? (
            <Text className="text-slate text-sm">
              {t("HINT_SUPER_ADMIN_CANNOT_DELETE_ACCOUNT")}
            </Text>
          ) : (
            <Text className="text-gray-dark">
              {t("HINT_DELETE_ACCOUNT_CANNOT_UNDO")}
            </Text>
          )}
        </View>
      </View>

      <ConfirmModal
        visible={showDelete}
        title="QUEST_DELETE_OWN_ACCOUNT"
        description="HINT_DELETE_OWN_ACCOUNT"
        confirmText="BTN_CONFIRM"
        cancelText="BTN_CANCEL"
        confirmationText={userEmail}
        confirmationLabelKey="HINT_CONFIRMATION_TYPE_EMAIL"
        confirmationLabelParams={{ email: userEmail }}
        onCancel={() => setShowDelete(false)}
        onConfirm={handleDelete}
        loading={isPending}
      />
    </View>
  );
};

export default Account;
